import React, {useEffect} from 'react';
import {Box, Button, Grid, Link, Typography} from '@mui/material';
import {
  DataGridProProps,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid-pro';
import DescriptionIcon from '@mui/icons-material/Description';
import clsx from 'clsx';
import GetAppIcon from '@mui/icons-material/GetApp';
import {useSnackbar} from 'notistack';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import {useClientFilesV2} from './useClientFilesV2';
import Loading from '../../components/Loading/Loading';
import {useAccountContext} from '../../contexts/AccountContext';
import {downloadBase64File} from './utilsV2';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import {renderCellDate} from '../../utils/renderCellDate';
import {StickyDataGrid} from '../../components/Common/StickyDataGrid';
import {FileUploadFormWrapped} from './FileUploadFormV2';
import {fileUploadValidatorV2} from './fileUploadValidatorV2';
import {useDocumentUploaderFormConfigs} from '../../hooks/useDocumentUploaderFormConfigs';
import {SaltusAny} from '../../utils/SaltusAny';
import {useTableStyles} from '../../components/useTableStyles';
import {GetClientFilesV2_clientFiles} from '../../schema-types/GetClientFilesV2';

type GridCellDocsV2Params = GridRenderCellParams<
  GetClientFilesV2_clientFiles,
  SaltusAny,
  SaltusAny,
  GridTreeNodeWithRender
>;

export const renderCellFileName = (params: GridCellDocsV2Params) => (
  <Typography style={{margin: '0.5rem'}}>{params.row.name}</Typography>
);

export const renderCellURL = (params: GridCellDocsV2Params) => (
  <Link
    href={params.row.url ?? undefined}
    download
    target="_blank"
    rel="noreferrer"
    data-testid="render-url-id"
    style={{margin: 'auto', display: 'flex'}}
  >
    <DescriptionIcon />
    <GetAppIcon />
  </Link>
);

export const useRenderCellDownload = (params: GridCellDocsV2Params) => {
  const {account} = useAccountContext();
  const {downloadClientFile, downloadClientFileData, downloadClientFileLoading} =
    useClientFilesV2();
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
    if (!downloadClientFileData) {
      return;
    }
    if (!downloadClientFileData.clientDocumentDownload?.ok) {
      enqueueSnackbar({
        message: `There was an error downloading this file: ${downloadClientFileData.clientDocumentDownload?.errors?.join(
          ', '
        )}`,
        variant: 'error',
        autoHideDuration: null,
      });
      return;
    }
    downloadBase64File({
      contentBase64: downloadClientFileData?.clientDocumentDownload?.content || '',
      contentType: downloadClientFileData?.clientDocumentDownload?.contentType || '',
      fileName: String(params.row.name),
    });
  }, [downloadClientFileData]);

  return (
    <Button
      onClick={async () => {
        await downloadClientFile({
          variables: {
            accountCuroId: account?.curoId,
            documentId: params.row.documentId as string,
          },
        });
      }}
      disabled={downloadClientFileLoading}
    >
      <DescriptionIcon />
      <GetAppIcon />
    </Button>
  );
};

const cfColumns: DataGridProProps['columns'] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 4,
    editable: false,
    sortable: false,
    renderCell: renderCellFileName,
  },
  {
    field: 'documentType',
    headerName: 'Document Type',
    flex: 1,
    editable: false,
    sortable: false,
    valueFormatter: (value: SaltusAny) => value ?? 'N/A',
  },
  {
    field: 'createdAt',
    headerName: 'Created On',
    flex: 1,
    editable: false,
    sortable: false,
    renderCell: renderCellDate,
  },
  {
    field: 'url',
    headerName: 'Download',
    flex: 0.5,
    editable: false,
    sortable: false,
    renderCell: useRenderCellDownload,
  },
];

export function ClientDocumentsPageV2() {
  const {clientFilesPDFOnly, clientFilesError, clientFilesLoading} = useClientFilesV2();
  const classes = useTableStyles();
  const {formConfig} = useDocumentUploaderFormConfigs();
  const {account} = useAccountContext();

  if (clientFilesLoading) {
    return <Loading entityName="documents" />;
  }

  return (
    <>
      <FileUploadFormWrapped
        formFields={[]}
        validate={(values) =>
          fileUploadValidatorV2({
            values,
            formConfig,
            shouldValidateFirstContact: !account ? true : !account.isTrust,
          })
        }
      />
      <Box p={1} />
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Panel
            title="Client Documents"
            style={{
              height: '100%',
            }}
            color={themeColors.lightAlert}
          >
            {clientFilesError ? (
              <ErrorMessage error={clientFilesError}>
                Unable to get client documents: {JSON.stringify(clientFilesError)}
              </ErrorMessage>
            ) : null}

            <StickyDataGrid
              columns={cfColumns}
              rows={!clientFilesError ? clientFilesPDFOnly : []} // swallowing common error: "Folder ID does not exist for account with curo_id..."
              rowHeight={60}
              sortModel={[{field: 'createdAt', sort: 'desc'}]}
              className={clsx([classes.table, classes.hideColumnMenu])}
            />
          </Panel>
        </Grid>
      </Grid>
    </>
  );
}
